<script setup lang="ts">
import type { FunctionalComponent } from 'vue'

/**
 * Centered empty/zero-state block: optional icon + title + description + actions. Consolidates the
 * many ad-hoc "No results / nothing here" blocks (search panel, list placeholders, collection
 * lists) onto one centered, role="status" shape. Text-only is the common case (just pass `title`);
 * pass `icon` for the icon-led search/empty variant, and use the default slot for a CTA.
 *
 * The bespoke multi-card onboarding state (CollectionRouteEmptyState) is intentionally NOT folded
 * in here — it's a distinct layout, not a zero-state message.
 */
interface Props {
  /** Optional leading icon (lucide component). Omit for text-only states. */
  icon?: FunctionalComponent
  title?: string
  description?: string
  /** Vertical breathing room. compact for dense inline lists, roomy for full panels. */
  size?: 'compact' | 'default' | 'roomy'
}
const props = withDefaults(defineProps<Props>(), { size: 'default' })

const padding = {
  compact: 'px-4 py-6',
  default: 'px-4 py-8',
  roomy: 'px-4 py-10',
}
</script>

<template>
  <div
    role="status"
    class="flex flex-col items-center text-center text-gray-500"
    :class="padding[props.size]"
  >
    <component :is="props.icon" v-if="props.icon" class="mb-2 h-8 w-8 text-gray-300" />
    <p v-if="props.title" class="text-sm font-medium text-gray-600">{{ props.title }}</p>
    <p v-if="props.description" class="mt-1 text-xs text-gray-400">{{ props.description }}</p>
    <!-- Optional CTA / extra content (e.g. a Refresh button or an "upload now" link). -->
    <div v-if="$slots.default" class="mt-3"><slot /></div>
  </div>
</template>
